/**
 * ClassificaCard — la classifica in home, sfogliabile per competizione.
 *
 * Andrea 2026-09-09: prima mostrava solo la prima competizione in corso
 * (il campionato). Ora si sfoglia come "Prossima sfida" — frecce +
 * pallini — tra campionato e classifica di OGNI girone di Champions.
 * Su mobile le frecce sono piccole per il pollice, quindi si sfoglia
 * anche trascinando la card (useSwipe).
 *
 * Le classifiche dei gironi si calcolano dalle partite `girone_a` /
 * `girone_b`: la view del backend aggrega tutta la competizione, quindi
 * con la fase a eliminazione in corso non sarebbe piu' la classifica del
 * girone (vedi features/common/classifica.ts).
 *
 * Andrea 2026-09-10: il POSTO occupato dal blocco resta quello della
 * classifica piu' lunga (il campionato), ma la card NON viene allungata:
 * un girone da 4 squadre finisce dopo la quarta riga e sotto si vede lo
 * sfondo della pagina. Cosi' il centro scommesse non salta su quando si
 * sfoglia. Lo spazio e' tenuto da una copia invisibile della classifica
 * piu' lunga: niente altezze in pixel scritte a mano, che si sfaserebbero
 * al primo cambio di font o di numero di squadre.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  useClassifica,
  useGironiCL,
  usePartiteCompetizione,
  type CompetizioneItem,
} from "../../hooks/useFase5";
import type { SquadraItem } from "../../hooks/useFase7";
import { useSwipe } from "../../hooks/useSwipe";
import {
  classificaConTutteLeSquadre,
  classificaDaPartite,
  squadreDaPartite,
  type RigaClassifica,
} from "../common/classifica";
import { tr } from "../../i18n";

interface ClassificaCardProps {
  /** Competizioni della stagione ancora non concluse. */
  competizioni: CompetizioneItem[];
  squadre: SquadraItem[];
  /** Per evidenziare la riga della squadra di chi guarda. */
  userId: string | undefined;
}

interface Vista {
  key: string;
  label: string;
  righe: RigaClassifica[];
}

// Andrea 2026-09-17: in coda anche i fantapunti subiti.
const GRID =
  "grid grid-cols-[1.5rem_1fr_1.8rem_2.4rem_2.9rem_2.9rem] gap-1 px-2 sm:grid-cols-[2rem_1fr_3rem_3rem_3.5rem_3.5rem] sm:gap-2 sm:px-3";

/** Righe mostrate al massimo (la classifica intera sta in /lega). */
const MAX_RIGHE = 8;

/** Classi della card: identiche per quella vera e per la copia che tiene
 * lo spazio, altrimenti l'altezza riservata non combacia. */
const CARD = "block bg-bg-card border border-primary/20 rounded-card overflow-hidden transition";

/** Tabella di una vista: intestazione + righe. */
function Tabella({
  vista,
  squadre,
  userId,
}: {
  vista: Vista;
  squadre: SquadraItem[];
  userId: string | undefined;
}) {
  return (
    <div>
      <div
        className={`${GRID} py-2 bg-primary-deep/40 text-[10px] text-fg-muted uppercase tracking-wider font-mono`}
      >
        <div>{tr("Pos")}</div>
        <div>{tr("Squadra")}</div>
        <div className="text-right">{tr("Pt")}</div>
        <div className="text-right">G</div>
        <div className="text-right" title={tr("Fantapunti fatti")}>
          {tr("F.Pt")}
        </div>
        <div className="text-right" title={tr("Fantapunti subiti")}>
          {tr("F.Sub")}
        </div>
      </div>
      {vista.righe.slice(0, MAX_RIGHE).map((r) => {
        const sq = squadre.find((s) => s.id === r.squadra_id);
        const isMia = sq?.presidente_user_id === userId;
        return (
          <div
            key={r.squadra_id}
            className={`${GRID} py-2 text-sm border-t border-primary/10 ${
              isMia ? "bg-accent/5 text-accent" : "text-fg"
            }`}
          >
            <div className="font-display text-fg-muted">{r.posizione}°</div>
            <div className="truncate">{sq?.nome ?? r.squadra_id.slice(0, 8)}</div>
            <div className="text-right font-mono font-bold text-xs sm:text-sm text-accent">
              {r.giocato ? r.punti : "—"}
            </div>
            {/* Andrea 2026-09-24: solo le giornate giocate (il "2/2" di
                prima contava due volte la stessa cosa). */}
            <div className="text-right font-mono text-xs sm:text-sm">{r.partite_giocate}</div>
            <div className="text-right font-mono text-xs sm:text-sm">
              {r.giocato ? r.fantapunti.toFixed(1) : "—"}
            </div>
            <div className="text-right font-mono text-xs sm:text-sm text-fg-muted">
              {r.giocato ? r.fantapunti_subiti.toFixed(1) : "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Pallini sotto la card: quante classifiche ci sono e dove siamo
 * (stesso linguaggio di "Prossima sfida"). */
function Pallini({
  viste,
  indice,
  onScegli,
}: {
  viste: Vista[];
  indice: number;
  onScegli?: (i: number) => void;
}) {
  return (
    <div className="flex justify-center gap-1.5 mt-2">
      {viste.map((v, i) => (
        <button
          key={v.key}
          type="button"
          onClick={() => onScegli?.(i)}
          aria-label={v.label}
          title={v.label}
          className={`h-1.5 rounded-full transition ${
            i === indice ? "w-6 bg-accent" : "w-3 bg-fg-muted/40"
          }`}
        />
      ))}
    </div>
  );
}

export function ClassificaCard({ competizioni, squadre, userId }: ClassificaCardProps) {
  const campionato = competizioni.find((c) => c.tipo === "campionato") ?? null;
  const cl = competizioni.find((c) => c.tipo === "champions_league") ?? null;

  const { data: classificaCamp } = useClassifica(campionato?.id ?? null);
  const { data: partiteCamp } = usePartiteCompetizione(campionato?.id ?? null);
  const { data: gironi } = useGironiCL(cl?.id ?? null);
  const { data: partiteCL } = usePartiteCompetizione(cl?.id ?? null);

  const nomeSquadra = (id: string): string =>
    squadre.find((s) => s.id === id)?.nome ?? id.slice(0, 8);

  const viste: Vista[] = [];
  if (campionato || squadre.length > 0) {
    // Squadre del campionato dal calendario; se il calendario non esiste
    // ancora, tutta la lega (pre-stagione).
    const ids = squadreDaPartite(partiteCamp);
    viste.push({
      key: campionato?.id ?? "lega",
      label: campionato?.nome ?? tr("Lega"),
      righe: classificaConTutteLeSquadre(
        classificaCamp,
        ids.length > 0 ? ids : squadre.map((s) => s.id),
        nomeSquadra,
      ),
    });
  }
  for (const g of gironi ?? []) {
    const fase = g.nome === "A" ? "girone_a" : "girone_b";
    viste.push({
      key: g.id,
      label: tr("Girone {{nome}}", { nome: g.nome }),
      righe: classificaDaPartite(
        g.partecipanti.map((p) => p.squadra_id),
        (partiteCL ?? []).filter((p) => p.fase === fase),
        nomeSquadra,
      ),
    });
  }

  const [indice, setIndice] = useState(0);
  useEffect(() => {
    setIndice((i) => (i < viste.length ? i : 0));
  }, [viste.length]);
  const vista = viste[indice] ?? null;
  const piuViste = viste.length > 1;
  /** La classifica piu' lunga: decide lo spazio riservato al blocco. */
  const vistaPiuLunga = viste.reduce<Vista | null>(
    (piu, v) => (piu && piu.righe.length >= v.righe.length ? piu : v),
    null,
  );
  const vaiA = (delta: number) => setIndice((i) => (i + delta + viste.length) % viste.length);
  const swipe = useSwipe({
    onAvanti: () => vaiA(1),
    onIndietro: () => vaiA(-1),
    attivo: piuViste,
  });

  const cardVera = (
    <div
      {...swipe.handlers}
      className="touch-pan-y select-none"
      style={{
        transform: `translateX(${swipe.offset}px)`,
        transition: swipe.inTrascinamento ? "none" : "transform 150ms",
      }}
    >
      <Link
        to="/lega"
        draggable={false}
        onClick={swipe.annullaClick}
        className={`${CARD} hover:border-accent/40 hover:bg-bg-card-hover`}
      >
        {!vista || vista.righe.length === 0 ? (
          <div className="p-6 text-center">
            <div className="text-3xl mb-2">🏆</div>
            <div className="text-fg-muted text-sm">{tr("Classifica non disponibile.")}</div>
            <div className="text-xs text-fg-muted mt-1">
              {tr("Apparirà qui appena la lega avrà un calendario.")}
            </div>
          </div>
        ) : (
          <Tabella vista={vista} squadre={squadre} userId={userId} />
        )}
      </Link>
    </div>
  );

  return (
    <div>
      {/* Andrea 2026-09-10: su desktop la riga e' divisa in tre —
          titolo, frecce al centro, tr("vedi tutta") a destra — cosi' il
          selettore non resta appiccicato al titolo. Su mobile resta
          tutto a sinistra, che a 375px lo spazio non c'e'. */}
      <div className="flex items-baseline mb-2 gap-2">
        <div className="flex items-baseline gap-2 min-w-0 md:flex-1">
          <h3 className="font-display text-lg tracking-wider text-fg shrink-0">
            {tr("CLASSIFICA")}
          </h3>
        </div>
        <div className="flex items-center gap-1 min-w-0 md:flex-1 md:justify-center">
          {vista && piuViste && (
            <span className="flex items-center gap-1 min-w-0">
              <button
                type="button"
                onClick={() => vaiA(-1)}
                aria-label={tr("Classifica precedente")}
                title={tr("Classifica precedente")}
                className="w-5 h-5 rounded-full bg-primary-deep/85 border border-accent/40 text-accent text-xs leading-none flex items-center justify-center hover:bg-primary-deep transition shrink-0"
              >
                ‹
              </button>
              {/* Andrea 2026-09-09: niente "1/3" accanto al nome — a 375px
                  mangiava lo spazio e l'etichetta finiva troncata. A dire
                  quante sono e dove siamo ci pensano i pallini. */}
              <span
                className="text-[10px] text-fg-muted uppercase font-mono truncate"
                title={tr("{{v1}} · {{v2}} {{v3}} {{v4}}", {
                  v1: vista.label,
                  v2: indice + 1,
                  v3: tr("di"),
                  v4: viste.length,
                })}
              >
                {vista.label}
              </span>
              <button
                type="button"
                onClick={() => vaiA(1)}
                aria-label={tr("Classifica successiva")}
                title={tr("Classifica successiva")}
                className="w-5 h-5 rounded-full bg-primary-deep/85 border border-accent/40 text-accent text-xs leading-none flex items-center justify-center hover:bg-primary-deep transition shrink-0"
              >
                ›
              </button>
            </span>
          )}
          {vista && !piuViste && (
            <span className="text-[10px] text-fg-muted uppercase font-mono truncate">
              {vista.label}
            </span>
          )}
        </div>
        <Link
          to="/lega"
          className="text-xs text-accent font-bold shrink-0 ml-auto md:ml-0 md:flex-1 md:text-right"
        >
          {tr("Vedi tutta →")}
        </Link>
      </div>

      {piuViste && vistaPiuLunga ? (
        // Copia invisibile = spazio riservato; la card vera ci sta dentro
        // ancorata in alto, con la sua altezza naturale.
        <div className="relative">
          <div aria-hidden="true" className="invisible">
            <div className={CARD}>
              <Tabella vista={vistaPiuLunga} squadre={squadre} userId={userId} />
            </div>
            <Pallini viste={viste} indice={indice} />
          </div>
          <div className="absolute inset-x-0 top-0">
            {cardVera}
            <Pallini viste={viste} indice={indice} onScegli={setIndice} />
          </div>
        </div>
      ) : (
        cardVera
      )}
    </div>
  );
}
